import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTicketAlt, FaChartLine, FaCalendarAlt, FaMoneyBillWave, FaClock, FaStar } from 'react-icons/fa';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import { logout } from '../../../../utils/api/auth';
import { fetchOrganiserTicketSalesAnalytics } from '../../../../utils/api/organiserApi';
import OverviewCards from '../../../../components/OverviewCards.jsx';
import AnalyticsCharts from '../../../../components/AnalyticsCharts.jsx';
import CollapsibleTable from '../../../../components/CollapsibleTable.jsx';
import TopEventsCards from '../../../../components/TopEventsCards.jsx';
import '../css/TicketSalesAnalytics.css';

const INITIAL_ANALYTICS_STATE = {
  totalTicketsSold: 0,
  totalRevenue: 0,
  averageTicketPrice: 0,
  bestSellingCategory: '',
  salesByCategory: [],
  salesTrends: [],
  pricingAnalytics: [],
  topSellingEvents: [],
};

function TicketSalesAnalytics() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(INITIAL_ANALYTICS_STATE);

  useEffect(() => {
    fetchTicketSalesData();
  }, []);

  const fetchTicketSalesData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[TicketSalesAnalytics] 🎟️ Fetching — API: fetchOrganiserTicketSalesAnalytics()');
      const response = await fetchOrganiserTicketSalesAnalytics();
      console.log('[TicketSalesAnalytics] ✅ Data loaded:', {
        columns: 'totalTicketsSold, totalRevenue, averageTicketPrice, bestSellingCategory, salesByCategory[{category,tickets,revenue,avgPrice,growth}], salesTrends[{month,tickets,avgPrice}], pricingAnalytics[{ticketPrice,tickets,revenue}], topSellingEvents',
        formula: 'marketShare% = (category.tickets / totalTicketsSold) * 100',
        data: response,
      });
      setAnalyticsData(response);
    } catch (error) {
      console.error('[TicketSalesAnalytics] ❌ Error fetching ticket sales data:', error);
      setError('Unable to load ticket sales data. Please try again later.');
      setAnalyticsData(INITIAL_ANALYTICS_STATE);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout(nav);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Prepare data for components
  const totalTicketsSold = Number(analyticsData.totalTicketsSold ?? 0);
  const overviewCards = [
    {
      icon: <FaTicketAlt />,
      title: 'Total Tickets Sold',
      value: totalTicketsSold.toLocaleString(),
      subtitle: 'All time sales',
      color: '#EFB034FF'
    },
    {
      icon: <FaMoneyBillWave />,
      title: 'Total Revenue',
      value: formatCurrency(analyticsData.totalRevenue),
      subtitle: 'From ticket sales',
      color: '#EFB034FF'
    },
    {
      icon: <FaChartLine />,
      title: 'Avg Ticket Price',
      value: formatCurrency(analyticsData.averageTicketPrice),
      subtitle: 'Per ticket',
      color: '#EFB034FF'
    },
    {
      icon: <FaStar />,
      title: 'Best Category',
      value: analyticsData.bestSellingCategory,
      subtitle: 'Top performing',
      color: '#EFB034FF'
    }
  ];

  const charts = [
    {
      type: 'line',
      data: analyticsData.salesTrends,
      title: 'Sales Trends (6 Months)',
      xAxisKey: 'month',
      lines: [
        { dataKey: 'tickets', color: '#EFB034FF', name: 'Tickets Sold' },
        { dataKey: 'avgPrice', color: '#c3e1f8', name: 'Avg Price' }
      ],
      height: 300
    },
    {
      type: 'bar',
      data: analyticsData.pricingAnalytics,
      title: 'Pricing Distribution',
      xAxisKey: 'ticketPrice',
      bars: [
        { dataKey: 'tickets', color: '#EFB034FF', name: 'Tickets Sold' },
        { dataKey: 'revenue', color: '#b8f5cd', name: 'Revenue' }
      ],
      height: 300
    }
  ];

  const salesByCategoryColumns = [
    {
      header: 'Category',
      key: 'category',
      render: (value) => (
        <span className="category-badge">{value}</span>
      )
    },
    {
      header: 'Tickets Sold',
      key: 'tickets',
      render: (value = 0) => Number(value).toLocaleString()
    },
    {
      header: 'Revenue',
      key: 'revenue',
      render: (value) => formatCurrency(value)
    },
    {
      header: 'Avg Price',
      key: 'avgPrice',
      render: (value) => formatCurrency(value)
    },
    {
      header: 'Growth',
      key: 'growth',
      render: (value, row) => {
        const className = value > 15 ? 'growth-indicator high' : value > 10 ? 'growth-indicator medium' : 'growth-indicator low';
        return <span className={className}>+{value}%</span>;
      }
    },
    {
      header: 'Market Share',
      key: 'tickets',
      render: (value) => {
        const base = totalTicketsSold > 0 ? totalTicketsSold : 1;
        const percentage = Math.round((Number(value ?? 0) / base) * 100);
        return (
          <div className="market-share">
            <div className="share-bar" style={{ width: `${percentage}%` }}></div>
            <span className="share-text">{percentage}%</span>
          </div>
        );
      }
    }
  ];

  if (error) {
    return (
      <div className="ticket-sales-analytics">
        <GlobalHeader
          title="Ticket Sales Analysis"
          subtitle="Unable to load ticket sales data"
          onLogout={handleLogout}
          backTo="/organiser-dash"
        />
        <div className="loading-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ticket-sales-analytics">
        <GlobalHeader
          title="Ticket Sales Analysis"
          subtitle="Loading ticket sales data..."
          onLogout={handleLogout}
          backTo="/organiser-dash"
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-sales-analytics">
      <GlobalHeader
        title="Ticket Sales Analysis"
        subtitle="Detailed ticket sales analysis by type, price, and timing"
        onLogout={handleLogout}
        backTo="/organiser-dash"
      />

      <main className="ticket-sales-analytics__main">
        {/* Overview Cards */}
        <OverviewCards cards={overviewCards} />

        {/* Top Selling Events */}
        <TopEventsCards
          events={analyticsData.topSellingEvents}
          formatCurrency={formatCurrency}
        />

        {/* Sales by Category Table */}
        {analyticsData.salesByCategory.length > 0 && (
          <CollapsibleTable
            title="Sales by Category"
            data={analyticsData.salesByCategory}
            columns={salesByCategoryColumns}
          />
        )}

        {/* Charts Section */}
        <section className="analytics-charts">
          <AnalyticsCharts charts={charts} />
        </section>
      </main>
    </div>
  );
}

export default TicketSalesAnalytics;
